import React from 'react'
import { Link, LinkProps } from 'react-router-dom'
import {
  ArticleCardFragment,
  ArticleCardAuthorFragment,
  ArticleCardAuthorProfileFragment
} from './ArticleCard.generated'
import { format } from 'date-fns'
import classnames from 'classnames'
import styles from './styles.css'
import { Avatar, BackgroundImage } from '@deal/components'

interface AuthorInfo {
  name?: string
  avatarUrl?: string
  profile?: ArticleCardAuthorProfileFragment
}

export function getAuthorInfo(author: ArticleCardAuthorFragment): AuthorInfo {
  switch (author.__typename) {
    case 'User':
      return {
        name: `${author.firstName || ''} ${author.lastName || ''}`.trim(),
        avatarUrl: author.profilePhoto?.url
      }
    case 'BusinessUser':
      return {
        name: author.displayName,
        avatarUrl:
          author.expertProfile?.profilePhoto?.url || author.articleAuthorProfile?.profilePhoto?.url,
        profile: author.expertProfile || author.articleAuthorProfile || undefined
      }
    case 'Operator':
      return getAuthorInfo(author.user)
    default:
      return {}
  }
}

interface AuthorInfoProps {
  article: ArticleCardFragment
  layout: 'horizontal' | 'vertical'
}

const AuthorInfo: React.FC<AuthorInfoProps> = ({ article, layout }) => {
  const firstAuthor = article.authors[0]

  if (!firstAuthor) {
    return null
  }

  const firstAuthorInfo = getAuthorInfo(firstAuthor)
  const otherAuthorsCount = article.authors.length - 1

  return layout === 'horizontal' ? (
    <div>{`by ${firstAuthorInfo.name}${
      otherAuthorsCount > 0 ? ` + ${otherAuthorsCount}` : ''
    } • ${format(article.updatedAt, 'M/dd/y')}`}</div>
  ) : (
    <div className={styles.authorContainer}>
      {firstAuthorInfo.name && (
        <Avatar name={firstAuthorInfo.name} src={firstAuthorInfo.avatarUrl} size={44} />
      )}
      <div>
        <div className={styles.author}>{`${firstAuthorInfo.name}${
          otherAuthorsCount > 1
            ? ` + ${otherAuthorsCount} others`
            : otherAuthorsCount === 1
            ? ' + 1 other'
            : ''
        }`}</div>
        <div>
          Updated{' '}
          <time
            itemProp="datePublished"
            dateTime={format(article.updatedAt, 'MM-dd-yyyy')}
            title={format(article.updatedAt, 'LLLL do, yyyy')}
          >
            {format(article.updatedAt, 'M/dd/y')}
          </time>
        </div>
      </div>
    </div>
  )
}
interface ArticleCardProps extends Partial<LinkProps> {
  article: ArticleCardFragment
  sizes: string
  layout?: 'horizontal' | 'vertical'
  className?: string
  hideSummary?: boolean
  showAuthor?: boolean
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  sizes,
  className,
  hideSummary = false,
  showAuthor,
  layout = 'vertical',
  ...linkProps
}) => {
  return (
    <Link
      to={`/journal/${article.friendlyId}/${article.slug}`}
      className={classnames(
        styles.articleCard,
        {
          [styles.horizontal]: layout === 'horizontal'
        },
        className
      )}
      {...linkProps}
    >
      <div className={styles.featuredImageWrapper}>
        <BackgroundImage
          src={article.featuredImage?.media.url || 'Placeholder Image...'}
          sizes={sizes}
          containerClassName={styles.featuredImageAspectRatioContainer}
          className={styles.featuredImage}
          id={`article-card-image-${article.articleId}`}
        />
      </div>
      <div className={styles.articleDetails}>
        {!showAuthor && (
          <div data-testid="article-updated-at" className={styles.updatedAt}>
            Updated{' '}
            <time
              itemProp="datePublished"
              dateTime={format(article.updatedAt, 'MM-dd-yyyy')}
              title={format(article.updatedAt, 'LLLL do, yyyy')}
            >
              {format(article.updatedAt, 'M/dd/y')}
            </time>
          </div>
        )}
        <div className={styles.title} data-testid="article-title">
          {article.title}
        </div>
        {!hideSummary && <div className={styles.summaryText}>{article.summaryText}</div>}
        {showAuthor && <AuthorInfo article={article} layout={layout} />}
      </div>
    </Link>
  )
}

export default ArticleCard
